import { Fragment, type ReactNode } from "react"
import { ItemMedia, LIBRARY_ITEM_ATTR, type LibraryItem } from "@/components/library/library-item"
import { LibraryItems, LibraryItemsReporter } from "@/components/library/library-items"
import { LibraryNodeEmpty, LibraryNodeError } from "@/components/library/library-node-state"
import { useTranslation } from "@/hooks/use-translation"
import { findEpisodePath, findScenePath, findShotPath } from "@/hooks/use-library-browser"
import {
  isLibraryFlowOrphan,
  type LibraryCanvasRow,
  type LibraryEntitySections,
  type LibraryFlowRow,
  type LibrarySearchAsset,
  type LibrarySearchPlace,
  type LibrarySearchResults as LibrarySearchResultsData,
  type LibrarySearchTarget,
  type LibraryTree as LibraryTreeData,
} from "@/lib/library-api"
import type { LibrarySelection } from "@/lib/library-url"
import type { LibraryViewMode } from "@/lib/library-view-mode"
import { cn } from "@/lib/utils"

type ResolvedPlace = {
  title: string
  /** Путь до папки от корня секции: рисуется мелким текстом под названием. */
  path: string[]
  posterUrl: string | null
  selection: LibrarySelection
}

type Props = {
  results: LibrarySearchResultsData
  query: string
  loading: boolean
  failed: boolean
  tree: LibraryTreeData
  canvases: LibraryCanvasRow[]
  flows: LibraryFlowRow[]
  entities: LibraryEntitySections
  mode: LibraryViewMode
  frameRate?: number
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpenItem?: (item: LibraryItem) => void
  onItemsChange?: (items: LibraryItem[]) => void
  onSelect: (selection: LibrarySelection) => void
  onRetry: () => void
}

const NO_ITEMS: LibraryItem[] = []

/** Подсветка совпавших слов во фрагменте: ищем те же подстроки, что искал сервер. */
function highlight(text: string, terms: string[]): ReactNode {
  if (terms.length === 0) return text
  const pattern = terms
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .sort((a, b) => b.length - a.length)
    .join("|")
  const parts = text.split(new RegExp(`(${pattern})`, "gi"))
  const lowered = terms.map((term) => term.toLowerCase())
  return parts.map((part, index) => (
    <Fragment key={index}>
      {lowered.includes(part.toLowerCase()) ? (
        // Ни горизонтальных полей, ни скругления: подложка обязана лежать ровно под
        // буквами, как выделение мышью. Пилюля с padding раздвигала слово, и «Дамблдор»
        // читался как «Дамбл дор».
        <mark className="bg-ui-accent-soft text-ui-accent-text">{part}</mark>
      ) : (
        part
      )}
    </Fragment>
  ))
}

function targetToSelection(target: LibrarySearchTarget): LibrarySelection | null {
  if (target.section === "canvas") return { section: "canvas", canvasId: target.canvas_id }
  if (target.section === "flow") {
    if ("orphans" in target) return { section: "flow", flowId: null, orphans: true }
    return { section: "flow", flowId: target.flow_id }
  }
  if (target.section === "sketches") return { section: "sketches" }
  if (target.section === "uploads") return { section: "uploads" }
  if (target.section !== "shots") return { section: target.section, entityId: target.entity_id }
  if (target.shot_id != null) return { section: "shots", node: { level: "shot", shotId: target.shot_id } }
  if (target.scene_id != null) return { section: "shots", node: { level: "scene", sceneId: target.scene_id } }
  if (target.episode_id != null) return { section: "shots", node: { level: "episode", episodeId: target.episode_id } }
  return null
}

/**
 * Название и путь берём из дерева, уже загруженного страницей, а не с сервера: так они
 * совпадают с крошками и с деревом слева слово в слово и не требуют локализации в API.
 * Дерево же и решает, что папка ещё существует: скрытый шот или заархивированная сцена
 * в него не попадают, и результат на них не показывается вовсе.
 */
function useResolvePlace(
  tree: LibraryTreeData,
  canvases: LibraryCanvasRow[],
  flows: LibraryFlowRow[],
  entities: LibraryEntitySections,
): (target: LibrarySearchTarget) => ResolvedPlace | null {
  const { t } = useTranslation(["workspace", "common"])

  return (target) => {
    const selection = targetToSelection(target)
    if (!selection) return null

    if (target.section === "canvas") {
      const board = canvases.find((row) => row.id === target.canvas_id)
      if (!board) return null
      return { title: board.title, path: [t("library.sections.canvas")], posterUrl: null, selection }
    }

    if (target.section === "flow") {
      if ("orphans" in target) {
        const row = flows.find(isLibraryFlowOrphan)
        if (!row) return null
        return { title: row.title, path: [t("library.sections.flow")], posterUrl: null, selection }
      }
      const board = flows.find((row) => !isLibraryFlowOrphan(row) && row.id === target.flow_id)
      if (!board) return null
      return { title: board.title, path: [t("library.sections.flow")], posterUrl: null, selection }
    }

    if (target.section === "sketches") {
      return {
        title: t("library.sections.sketches"),
        path: [t("library.sections.sketches")],
        posterUrl: null,
        selection,
      }
    }

    if (target.section === "uploads") {
      return {
        title: t("library.sections.uploads"),
        path: [t("library.sections.uploads")],
        posterUrl: null,
        selection,
      }
    }

    if (target.section !== "shots") {
      const row = entities[target.section].find((entity) => entity.id === target.entity_id)
      if (!row) return null
      return {
        title: row.name ?? `#${row.id}`,
        path: [t(`library.sections.${target.section}`)],
        posterUrl: row.poster_url,
        selection,
      }
    }

    if (target.shot_id != null) {
      const path = findShotPath(tree, target.shot_id)
      if (!path) return null
      return {
        title: t("library.crumbs.shot", { number: `${path.scene.number}.${path.shot.number}` }),
        path: [
          t("library.sections.shots"),
          `S${path.season.number}E${path.episode.number}`,
          t("library.crumbs.scene", { number: path.scene.number }),
        ],
        posterUrl: path.shot.poster_url,
        selection,
      }
    }

    if (target.scene_id != null) {
      const path = findScenePath(tree, target.scene_id)
      if (!path) return null
      return {
        title: path.scene.title || t("library.crumbs.scene", { number: path.scene.number }),
        path: [t("library.sections.shots"), `S${path.season.number}E${path.episode.number}`],
        posterUrl: null,
        selection,
      }
    }

    const path = target.episode_id != null ? findEpisodePath(tree, target.episode_id) : null
    if (!path) return null
    return {
      title: path.episode.title || `S${path.season.number}E${path.episode.number}`,
      path: [t("library.sections.shots"), `S${path.season.number}E${path.episode.number}`],
      posterUrl: null,
      selection,
    }
  }
}

function searchAssetToItem(asset: LibrarySearchAsset, folderTitle: string | null, frameRate?: number): LibraryItem {
  const video = asset.media === "video"
  return {
    key: asset.key,
    kind: asset.media,
    label: asset.label,
    // В выдаче важнее не таймкод, а откуда ассет: одинаковых имён файлов в проекте много.
    meta: folderTitle,
    createdAt: asset.created_at,
    thumbUrl: asset.thumb_url,
    source: asset.source ?? undefined,
    asset: {
      title: asset.label,
      kind: asset.media,
      url: video ? null : asset.url,
      videoUrl: video ? asset.url : null,
      downloadUrl: asset.download_url ?? asset.url,
      createdAt: asset.created_at,
      durationMs: asset.duration_ms,
      frameRate: frameRate ?? null,
    },
  }
}

/**
 * Подпись совпавшего поля. Словарь перечислен целиком, а не собран шаблонной строкой:
 * ключи переводов типизированы, и `library.search.field.${field}` типом не проверяется —
 * опечатка в поле дошла бы до экрана.
 */
function useMatchFieldLabel(): (field: string) => string {
  const { t } = useTranslation(["workspace"])
  const labels: Record<string, string> = {
    storyboardPrompt: t("library.search.field.storyboardPrompt"),
    videoPrompt: t("library.search.field.videoPrompt"),
    intent: t("library.search.field.intent"),
    promptSummary: t("library.search.field.promptSummary"),
    title: t("library.search.field.title"),
    name: t("library.search.field.name"),
    outfit: t("library.search.field.outfit"),
    variant: t("library.search.field.variant"),
    viewpoint: t("library.search.field.viewpoint"),
    state: t("library.search.field.state"),
    prompt: t("library.search.field.prompt"),
    filename: t("library.search.field.filename"),
  }
  return (field) => labels[field] ?? field
}

function SearchSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="mb-5 last:mb-0">
      <h2 className="mb-2 text-[10px] tracking-[0.09em] text-ui-text-faint uppercase">{label}</h2>
      {children}
    </section>
  )
}

function PlaceRow({
  place,
  resolved,
  terms,
  focused,
  onFocus,
  onOpen,
}: {
  place: LibrarySearchPlace
  resolved: ResolvedPlace
  terms: string[]
  focused: boolean
  onFocus: () => void
  onOpen: () => void
}) {
  const fieldLabel = useMatchFieldLabel()
  return (
    <button
      type="button"
      {...{ [LIBRARY_ITEM_ATTR]: place.key }}
      tabIndex={focused ? 0 : -1}
      onClick={() => {
        onFocus()
        onOpen()
      }}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border border-transparent px-2 py-2 text-left transition-colors",
        "hover:border-ui-border hover:bg-ui-surface-raised",
        focused && "ring-2 ring-ui-accent outline-none ring-inset",
      )}
    >
      <ItemMedia
        item={{ key: place.key, kind: "folder", label: resolved.title, thumbUrl: resolved.posterUrl }}
        className="h-9 w-14 shrink-0 rounded"
      />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm text-ui-text-primary">{resolved.title}</span>
          <span className="truncate text-[11px] text-ui-text-muted">{resolved.path.join(" · ")}</span>
        </span>
        <span className="mt-0.5 flex min-w-0 items-baseline gap-1.5">
          <span className="shrink-0 text-[10px] tracking-[0.06em] text-ui-text-faint uppercase">
            {fieldLabel(place.match.field)}
          </span>
          <span className="truncate text-xs text-ui-text-secondary">{highlight(place.match.snippet, terms)}</span>
        </span>
      </span>
    </button>
  )
}

/**
 * Выдача поиска на месте содержимого папки. Два блока: папки, у которых совпал
 * собственный текст, и ассеты, совпавшие содержимым, — вторые остаются обычными
 * плитками, поэтому просмотрщик, клавиатура и выбор в пикере работают в выдаче
 * ровно так же, как в папке.
 */
export function LibrarySearchResults({
  results,
  query,
  loading,
  failed,
  tree,
  canvases,
  flows,
  entities,
  mode,
  frameRate,
  focusedKey,
  onFocusItem,
  onOpenItem,
  onItemsChange,
  onSelect,
  onRetry,
}: Props) {
  const { t } = useTranslation(["workspace", "common"])
  const resolvePlace = useResolvePlace(tree, canvases, flows, entities)
  const terms = query.trim().split(/\s+/).filter(Boolean)

  const places = results.places.flatMap((place) => {
    const resolved = resolvePlace(place.target)
    return resolved ? [{ place, resolved }] : []
  })
  const assets = results.assets.map((asset) =>
    searchAssetToItem(asset, resolvePlace(asset.target)?.title ?? null, frameRate),
  )

  if (failed) {
    return (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        <LibraryNodeError onRetry={onRetry} />
      </>
    )
  }

  if (places.length === 0 && assets.length === 0) {
    return (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        <LibraryNodeEmpty>
          {loading ? t("library.search.searching") : t("library.search.empty", { query: query.trim() })}
        </LibraryNodeEmpty>
      </>
    )
  }

  return (
    <div className="p-3">
      {/* Клавиатурный обход идёт по DOM-порядку, поэтому и репортим так же: сперва
          папки, потом ассеты. Папки в список не попадают — открывать в просмотрщике
          у них нечего. */}
      <LibraryItemsReporter items={assets} onItemsChange={onItemsChange} />
      {places.length > 0 ? (
        <SearchSection label={t("library.search.placesGroup")}>
          <div className="flex flex-col gap-0.5">
            {places.map(({ place, resolved }) => (
              <PlaceRow
                key={place.key}
                place={place}
                resolved={resolved}
                terms={terms}
                focused={focusedKey === place.key}
                onFocus={() => onFocusItem?.(place.key)}
                onOpen={() => onSelect(resolved.selection)}
              />
            ))}
          </div>
        </SearchSection>
      ) : null}
      {assets.length > 0 ? (
        <SearchSection label={t("library.search.assetsGroup")}>
          <LibraryItems
            items={assets}
            mode={mode}
            listLayout="table"
            aspectClass="aspect-square"
            focusedKey={focusedKey}
            onFocusItem={onFocusItem}
            onOpenItem={onOpenItem}
          />
        </SearchSection>
      ) : null}
      {results.truncated ? <p className="pt-1 text-xs text-ui-text-muted">{t("library.search.truncated")}</p> : null}
    </div>
  )
}
